/**
 * @file The mobile-microphone QR link — the one place its shape is decided.
 * @module utils/consult/mobileMicLink
 *
 * S34 of ClickUp 86d3zmpwv. Sibling of `participantIdentity` (S8), `audioSettings` (S23) and
 * `speakerNames` (S25).
 *
 * ═══ THE ROOM NUMBER STOPPED BEING THE CREDENTIAL ═════════════════════════════════════════
 * The QR used to be `${origin}/mobile-mic?roomId=${room_number}` and that was the whole of it:
 * anyone who read the number off the practitioner's screen, photographed the code or guessed it
 * could open the page and stream audio into a live consultation — audio that becomes the
 * transcript the AI consult report is written from.
 *
 * It now carries a short-lived token talk mints for that one room (`POST /api/mobile-mic-token`,
 * 15 minutes, HMAC-signed), which the phone presents on the socket handshake. `roomId` stays in
 * the URL because `MobileMic.tsx` needs it before the socket exists — it is a parameter now, not
 * a credential.
 *
 * ═══ THE TOKEN TRAVELS IN THE FRAGMENT, NEVER THE QUERY STRING ════════════════════════════
 * A query string is written in full to the nginx ingress access log of every request the phone
 * makes, and to the phone's own history. A fragment is never sent to a server at all — the browser
 * strips it before the request — so `#mic=…` keeps a live microphone credential out of every access
 * log between the phone and the pod. The QR scans identically either way; the phone reads it back
 * with `readMobileMicToken()` and drops it from the address bar.
 *
 * ⚠️ This is the FRONTEND half only. The token's format, its 15 minutes and the handshake field it
 * is presented in (`handshake.auth.micToken`) are unchanged — talk never saw the URL.
 *
 * ═══ WHY A FUNCTION AND NOT A TEMPLATE LITERAL IN THE PAGE ════════════════════════════════
 * So the shape can be asserted without mounting a 40 KB consultation page: the QR is the only
 * carrier of a bearer credential in this app that a human points a camera at, and "the token
 * reached the QR" is exactly the thing worth pinning.
 */

/** The key the token travels under, so the page and the phone cannot disagree. */
export const MOBILE_MIC_TOKEN_PARAM = "mic";

/**
 * The URL encoded into the mobile-microphone QR.
 *
 * Both values are URL-encoded: a room number is clinic-chosen free text, and the token's base64url
 * signature is encoding-safe but its room-id prefix is not.
 *
 * @param origin — `window.location.origin` of the clinic frontend the phone will open
 * @param roomId — the consult's room number, in the QUERY string: it is a parameter, not a
 *                 credential, and the page needs it before the socket exists
 * @param token — the mint from talk, in the FRAGMENT so it reaches no access log. Omitted, the link
 *                is deliberately unusable: talk refuses a handshake carrying no credential, so a
 *                tokenless QR is a slow failure on the phone rather than a working one. Never
 *                render the QR before the token arrives.
 */
export const buildMobileMicUrl = (origin: string, roomId: string, token?: string | null): string => {
	const url = `${origin}/mobile-mic?${new URLSearchParams({ roomId }).toString()}`;
	if (!token) {
		return url;
	}
	return `${url}#${new URLSearchParams({ [MOBILE_MIC_TOKEN_PARAM]: token }).toString()}`;
};

/**
 * The token out of a URL fragment — `location.hash`, with or without its leading `#`.
 *
 * The same `URLSearchParams` encoding the builder used, so a room number carrying a space or an
 * ampersand survives the round trip.
 *
 * @returns the token, or `""` when the fragment carries none — a QR photographed before this
 *          shipped has it in the query string instead, and the page says so rather than hanging.
 */
export const readMobileMicToken = (hash: string): string => {
	const fragment = hash.startsWith("#") ? hash.slice(1) : hash;
	return new URLSearchParams(fragment).get(MOBILE_MIC_TOKEN_PARAM) || "";
};
